// src/services/api.js
const API_BASE_URL = process.env.REACT_APP_GOOGLE_SCRIPT_URL;

console.log('🔗 API Base URL loaded:', API_BASE_URL);

class TradingAPI {
  constructor() {
    this.baseUrl = API_BASE_URL;
    console.log('🚀 TradingAPI initialized with URL:', this.baseUrl);
  }

  async makeRequest(url, options = {}) {
    try {
      console.log('📡 Making request to:', url);
      
      if (!this.baseUrl) {
        throw new Error('REACT_APP_GOOGLE_SCRIPT_URL environment variable is not set');
      }
      
      const response = await fetch(url, {
        method: 'GET',
        mode: 'cors',
        headers: {
          'Content-Type': 'text/plain;charset=utf-8',
        },
        ...options,
      });

      console.log('📡 Response status:', response.status);
      
      if (!response.ok) {
        if (response.status === 302) {
          throw new Error('Script not properly deployed or access denied');
        }
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      console.log('📊 Raw API Response:', data);
      return data;
    } catch (error) {
      console.error('❌ API request failed:', error);
      throw new Error(`API Error: ${error.message}`);
    }
  }

  async getUserSettings(userId) {
    try {
      console.log('👤 Getting user settings for:', userId);
      
      const params = new URLSearchParams({
        action: 'getUserSettings',
        userId: userId
      });

      const url = `${this.baseUrl}?${params.toString()}`;
      const response = await this.makeRequest(url);
      
      // Handle various response formats from Google Apps Script
      if (response && typeof response === 'object') {
        // If it's already in the expected format
        if (response.success === true && response.data) {
          return response;
        }
        
        // If it's direct data without wrapper
        const userData = {
          userId: response.userId || userId,
          email: response.email || 'Unknown',
          accountNumber: response.accountNumber || 'Not set',
          broker: response.broker || 'Unknown',
          lotMultiplier: parseFloat(response.lotMultiplier) || 1.0,
          maxTrades: parseInt(response.maxTrades) || 10,
          status: response.status || 'active',
          totalTrades: parseInt(response.totalTrades) || 0,
          subscriptionType: response.subscriptionType || 'trial',
          expiryDate: response.expiryDate,
          registrationDate: response.registrationDate,
          lastActive: response.lastActive
        };

        return {
          success: true,
          message: 'User settings retrieved',
          data: {
            ...userData,
            stats: {
              totalTrades: userData.totalTrades,
              successRate: 0,
              totalProfit: 0,
              activePositions: 0
            }
          }
        };
      }
      
      throw new Error('Invalid response format');
    } catch (error) {
      console.error('❌ Get user settings failed:', error);
      throw error;
    }
  }

  async checkSubscription(userId) {
    try {
      console.log('💎 Checking subscription for:', userId);
      
      const params = new URLSearchParams({
        action: 'checkSubscription',
        userId: userId
      });

      const url = `${this.baseUrl}?${params.toString()}`;
      const response = await this.makeRequest(url);
      
      // Handle subscription response
      if (response && response.success === true && response.data?.subscription) {
        return response;
      } else if (response && response.subscription) {
        // Direct subscription data
        return {
          success: true,
          message: 'Subscription status retrieved',
          data: {
            subscription: response.subscription
          }
        };
      } else {
        // Default subscription for fallback
        return {
          success: true,
          message: 'Using default subscription',
          data: {
            subscription: {
              status: 'active',
              expiryDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString(),
              daysRemaining: 30,
              subscriptionType: 'trial'
            }
          }
        };
      }
    } catch (error) {
      console.error('❌ Check subscription failed:', error);
      // Return default active subscription instead of throwing
      return {
        success: true,
        message: 'Using default subscription (connection failed)',
        data: {
          subscription: {
            status: 'active',
            expiryDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString(),
            daysRemaining: 30,
            subscriptionType: 'trial'
          }
        }
      };
    }
  }

  async getPendingTrades(userId) {
    try {
      console.log('⚡ Getting pending trades for:', userId);
      
      const params = new URLSearchParams({
        action: 'getPendingTrades',
        userId: userId
      });

      const url = `${this.baseUrl}?${params.toString()}`;
      const response = await this.makeRequest(url);
      
      // Handle trades response
      if (response && response.success === true && response.data?.trades) {
        return response;
      } else if (response && response.trades && Array.isArray(response.trades)) {
        return { 
          success: true, 
          data: { trades: response.trades }
        };
      } else {
        return { 
          success: true, 
          message: 'No pending trades',
          data: { trades: [] }
        };
      }
    } catch (error) {
      console.error('❌ Get pending trades failed:', error);
      return { success: true, data: { trades: [] } };
    }
  }

  async getSystemStatus() {
    try {
      console.log('🔧 Getting system status...');
      
      const params = new URLSearchParams({
        action: 'systemStatus'
      });

      const url = `${this.baseUrl}?${params.toString()}`;
      const response = await this.makeRequest(url);
      
      return { 
        success: true, 
        message: 'System status retrieved',
        data: { 
          systemEnabled: response.systemEnabled !== false,
          serverTime: response.serverTime || new Date().toISOString(),
          version: response.version || 'Unknown',
          totalUsers: response.totalUsers || 0,
          maintenanceMode: response.maintenanceMode || false
        }
      };
    } catch (error) {
      console.error('❌ Get system status failed:', error);
      return { 
        success: true, 
        data: { 
          systemEnabled: true,
          serverTime: new Date().toISOString(),
          version: 'Unknown'
        }
      };
    }
  }

  async registerUser(userData) {
    try {
      console.log('👤 Registering user:', userData);
      
      const params = new URLSearchParams({
        action: 'registerUser',
        email: userData.email,
        accountNumber: userData.accountNumber,
        broker: userData.broker,
        lotMultiplier: userData.lotMultiplier.toString(),
        subscriptionType: userData.subscriptionType
      });

      const url = `${this.baseUrl}?${params.toString()}`;
      return await this.makeRequest(url);
    } catch (error) {
      console.error('❌ Registration API failed:', error);
      throw error;
    }
  }

  async testConnection() {
    try {
      console.log('🔄 Testing API connection...');
      console.log('📍 API Base URL:', this.baseUrl);
      
      if (!this.baseUrl) {
        throw new Error('REACT_APP_GOOGLE_SCRIPT_URL environment variable is not set');
      }

      const testUrl = `${this.baseUrl}?action=testConnection&test=true`;
      console.log('🔗 Test URL:', testUrl);
      
      const response = await fetch(testUrl, {
        method: 'GET',
        mode: 'cors',
        headers: {
          'Content-Type': 'text/plain;charset=utf-8',
        },
      });
      
      console.log('📡 Response status:', response.status);
      
      if (response.ok) {
        const data = await response.json();
        console.log('✅ Connection test successful:', data);
        return { success: true, message: 'API connection successful', data };
      } else {
        const errorText = await response.text();
        console.log('❌ Response body:', errorText);
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
    } catch (error) {
      console.error('❌ API connection test failed:', error);
      return { success: false, message: error.message };
    }
  }

  // Additional debugging methods
  async emergencyFix() {
    try {
      const url = `${this.baseUrl}?action=emergencyFix`;
      return await this.makeRequest(url);
    } catch (error) {
      console.error('❌ Emergency fix failed:', error);
      throw error;
    }
  }

  async testUser() {
    try {
      const url = `${this.baseUrl}?action=testUser`;
      return await this.makeRequest(url);
    } catch (error) {
      console.error('❌ Test user failed:', error);
      throw error;
    }
  }

  async fixAll() {
    try {
      const url = `${this.baseUrl}?action=fixAll`;
      return await this.makeRequest(url);
    } catch (error) {
      console.error('❌ Fix all failed:', error);
      throw error;
    }
  }

  async debugUser(userId) {
    try {
      const params = new URLSearchParams({
        action: 'debugUser',
        userId: userId
      });
      const url = `${this.baseUrl}?${params.toString()}`;
      return await this.makeRequest(url);
    } catch (error) {
      console.error('❌ Debug user failed:', error);
      throw error;
    }
  }
}

// Create and export the instance
export const tradingApi = new TradingAPI();

// Export additional utility functions
export const testApiConnection = () => tradingApi.testConnection();
export const emergencyFix = () => tradingApi.emergencyFix();
export const testUser = () => tradingApi.testUser();
export const fixAll = () => tradingApi.fixAll();

// Default export
export default tradingApi;

// Log that the module has loaded
console.log('✅ TradingAPI module loaded successfully');
console.log('🔍 Available methods:', Object.getOwnPropertyNames(TradingAPI.prototype));